import { useEffect, useState, useCallback } from 'react'
import { Download, RefreshCw, Plus, X, Check, AlertTriangle } from 'lucide-react'
import toast from 'react-hot-toast'
import { otaService, type OTAReleaseCreate } from '../../../services/otaService'
import { platformAnalyticsService } from '../../../services/platformAnalyticsService'
import type { OTARelease, OTARolloutResponse, ReleaseType } from '../../../types/api.types'

const RELEASE_TYPE_COLORS: Record<ReleaseType, string> = {
  STABLE:         'bg-green-100 text-green-800',
  BETA:           'bg-blue-100 text-blue-800',
  CRITICAL_PATCH: 'bg-red-100 text-red-800',
}

function fmtDate(ts: string): string {
  return new Date(ts).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

interface PublishModalProps {
  onClose: () => void
  onPublished: (r: OTARelease) => void
}

function PublishModal({ onClose, onPublished }: PublishModalProps): JSX.Element {
  const [form, setForm] = useState<OTAReleaseCreate>({
    version: '',
    release_type: 'STABLE',
    release_notes: '',
    download_url: '',
    checksum_sha256: '',
    min_hardware_version: '',
    is_active: true,
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  function set(key: keyof OTAReleaseCreate, val: string | boolean | null) {
    setForm((f) => ({ ...f, [key]: val }))
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true); setError('')
    try {
      const release = await otaService.create({
        ...form,
        min_hardware_version: form.min_hardware_version || null,
        release_notes: form.release_notes || null,
      })
      onPublished(release)
      toast.success(`Release ${release.version} published.`)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to publish release')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[90dvh] w-full max-w-lg overflow-y-auto rounded-xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <h2 className="text-[16px] font-bold text-gray-900">Publish New Release</h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4 p-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1 block text-[12px] font-medium text-gray-700">Version *</label>
              <input
                required
                value={form.version}
                onChange={(e) => set('version', e.target.value)}
                placeholder="2.3.0"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-[13px] outline-none focus:border-brand-green"
              />
            </div>
            <div>
              <label className="mb-1 block text-[12px] font-medium text-gray-700">Type *</label>
              <select
                value={form.release_type}
                onChange={(e) => set('release_type', e.target.value as ReleaseType)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-[13px] outline-none focus:border-brand-green"
              >
                <option value="STABLE">Stable</option>
                <option value="BETA">Beta</option>
                <option value="CRITICAL_PATCH">Critical Patch</option>
              </select>
            </div>
          </div>

          <div>
            <label className="mb-1 block text-[12px] font-medium text-gray-700">Download URL *</label>
            <input
              required
              value={form.download_url}
              onChange={(e) => set('download_url', e.target.value)}
              placeholder="https://cdn.quickcomm.io/firmware/2.3.0.bin"
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-[13px] outline-none focus:border-brand-green"
            />
          </div>

          <div>
            <label className="mb-1 block text-[12px] font-medium text-gray-700">SHA-256 Checksum *</label>
            <input
              required
              value={form.checksum_sha256}
              onChange={(e) => set('checksum_sha256', e.target.value)}
              placeholder="e3b0c44298fc1c..."
              className="w-full rounded-lg border border-gray-200 px-3 py-2 font-mono text-[12px] outline-none focus:border-brand-green"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1 block text-[12px] font-medium text-gray-700">Min Hardware Version</label>
              <input
                value={form.min_hardware_version ?? ''}
                onChange={(e) => set('min_hardware_version', e.target.value)}
                placeholder="1.0.0 (optional)"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-[13px] outline-none focus:border-brand-green"
              />
            </div>
            <div className="flex items-end pb-2">
              <label className="flex cursor-pointer items-center gap-2 text-[13px] text-gray-700">
                <input
                  type="checkbox"
                  checked={form.is_active ?? true}
                  onChange={(e) => set('is_active', e.target.checked)}
                  className="h-4 w-4 rounded"
                />
                Active (visible to devices)
              </label>
            </div>
          </div>

          <div>
            <label className="mb-1 block text-[12px] font-medium text-gray-700">Release Notes</label>
            <textarea
              rows={3}
              value={form.release_notes ?? ''}
              onChange={(e) => set('release_notes', e.target.value)}
              placeholder="What changed in this release..."
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-[13px] outline-none focus:border-brand-green"
            />
          </div>

          {error && <p className="text-[12px] text-red-500">{error}</p>}

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="rounded-lg border border-gray-200 px-4 py-2 text-[13px] text-gray-600 hover:bg-gray-50">Cancel</button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-1.5 rounded-lg bg-brand-green px-4 py-2 text-[13px] font-semibold text-black hover:opacity-80 disabled:opacity-50"
            >
              {saving ? <RefreshCw size={13} className="animate-spin" /> : <Check size={13} />}
              Publish
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export function OTAManagementPage(): JSX.Element {
  const [releases, setReleases] = useState<OTARelease[]>([])
  const [rollout, setRollout] = useState<OTARolloutResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [showPublish, setShowPublish] = useState(false)
  const [yanking, setYanking] = useState<number | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    const [relResult, rolloutResult] = await Promise.allSettled([
      otaService.list(),
      platformAnalyticsService.getOTARollout(),
    ])
    if (relResult.status === 'fulfilled') setReleases(relResult.value)
    if (rolloutResult.status === 'fulfilled') setRollout(rolloutResult.value)
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])

  async function yank(release: OTARelease) {
    setYanking(release.id)
    try {
      const updated = await otaService.update(release.id, { is_active: false })
      setReleases((prev) => prev.map((r) => r.id === updated.id ? updated : r))
      toast.success(`Release ${release.version} yanked.`)
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Failed to yank release')
    } finally {
      setYanking(null)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">OTA Management</h1>
          <p className="mt-1 text-sm text-gray-500">Firmware releases and device rollout status</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-[13px] text-gray-600 hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
          <button
            type="button"
            onClick={() => setShowPublish(true)}
            className="flex items-center gap-1.5 rounded-lg bg-brand-green px-3 py-2 text-[13px] font-semibold text-black hover:opacity-80"
          >
            <Plus size={14} />
            Publish Release
          </button>
        </div>
      </div>

      {/* Rollout summary */}
      {rollout && (
        <div className="rounded-xl border border-gray-200 bg-white p-6">
          <div className="mb-4 flex items-center gap-2">
            <Download size={15} className="text-brand-green" />
            <h2 className="text-[15px] font-bold text-gray-900">Rollout Status</h2>
            {rollout.latest_version && (
              <span className="ml-2 rounded-full bg-green-100 px-2.5 py-0.5 text-[12px] font-semibold text-green-800">
                Latest: {rollout.latest_version}
              </span>
            )}
          </div>
          <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
              <p className="text-[11px] text-gray-500">Total Devices</p>
              <p className="mt-1 text-[22px] font-bold text-gray-900">{rollout.total_devices}</p>
            </div>
            {Object.entries(rollout.version_distribution)
              .sort((a, b) => b[1] - a[1])
              .slice(0, 3)
              .map(([v, count]) => (
                <div key={v} className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                  <p className="text-[11px] text-gray-500">v{v}</p>
                  <p className="mt-1 text-[22px] font-bold text-gray-900">{count}</p>
                  <p className="text-[11px] text-gray-400">
                    {rollout.total_devices ? Math.round((count / rollout.total_devices) * 100) : 0}%
                  </p>
                </div>
              ))}
          </div>

          {rollout.organizations.length > 0 && (
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="w-full min-w-[640px]">
                <thead>
                  <tr className="bg-gray-50 text-left">
                    <th className="px-4 py-2.5 text-[11px] font-medium uppercase tracking-wide text-gray-500">Organisation</th>
                    <th className="px-4 py-2.5 text-center text-[11px] font-medium uppercase tracking-wide text-gray-500">Total</th>
                    <th className="px-4 py-2.5 text-center text-[11px] font-medium uppercase tracking-wide text-green-600">Up to Date</th>
                    <th className="px-4 py-2.5 text-center text-[11px] font-medium uppercase tracking-wide text-amber-600">Behind</th>
                    <th className="px-4 py-2.5 text-center text-[11px] font-medium uppercase tracking-wide text-blue-600">In Progress</th>
                    <th className="px-4 py-2.5 text-center text-[11px] font-medium uppercase tracking-wide text-red-500">Failed</th>
                  </tr>
                </thead>
                <tbody>
                  {rollout.organizations.map((org) => (
                    <tr key={org.organization_id} className="border-t border-gray-100 hover:bg-gray-50/40">
                      <td className="px-4 py-3 text-[13px] font-medium text-gray-900">{org.organization_name}</td>
                      <td className="px-4 py-3 text-center text-[13px] text-gray-700">{org.total_devices}</td>
                      <td className="px-4 py-3 text-center text-[13px] font-semibold text-green-600">{org.up_to_date}</td>
                      <td className="px-4 py-3 text-center text-[13px] text-amber-600">{org.behind}</td>
                      <td className="px-4 py-3 text-center text-[13px] text-blue-600">{org.in_progress}</td>
                      <td className="px-4 py-3 text-center text-[13px] text-red-500">{org.failed}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Releases list */}
      <div className="rounded-xl border border-gray-200 bg-white">
        <div className="border-b border-gray-100 px-6 py-4">
          <h2 className="text-[15px] font-bold text-gray-900">All Releases</h2>
        </div>
        {loading ? (
          <p className="py-12 text-center text-[13px] text-gray-400">Loading...</p>
        ) : releases.length === 0 ? (
          <p className="py-12 text-center text-[13px] text-gray-400">No releases published yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px]">
              <thead>
                <tr className="bg-gray-50 text-left">
                  <th className="px-4 py-2.5 text-[11px] font-medium uppercase tracking-wide text-gray-500">Version</th>
                  <th className="px-4 py-2.5 text-[11px] font-medium uppercase tracking-wide text-gray-500">Type</th>
                  <th className="px-4 py-2.5 text-[11px] font-medium uppercase tracking-wide text-gray-500">Released</th>
                  <th className="px-4 py-2.5 text-[11px] font-medium uppercase tracking-wide text-gray-500">Min HW</th>
                  <th className="px-4 py-2.5 text-[11px] font-medium uppercase tracking-wide text-gray-500">Notes</th>
                  <th className="px-4 py-2.5 text-[11px] font-medium uppercase tracking-wide text-gray-500">Status</th>
                  <th className="px-4 py-2.5 text-[11px] font-medium uppercase tracking-wide text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {releases.map((r) => (
                  <tr key={r.id} className="border-t border-gray-100 hover:bg-gray-50/40">
                    <td className="px-4 py-3">
                      <span className="font-mono text-[13px] font-semibold text-gray-900">{r.version}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${RELEASE_TYPE_COLORS[r.release_type]}`}>
                        {r.release_type.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-[13px] text-gray-600">{fmtDate(r.released_at)}</td>
                    <td className="px-4 py-3 text-[12px] text-gray-500">{r.min_hardware_version ?? '—'}</td>
                    <td className="max-w-xs px-4 py-3 text-[12px] text-gray-500">
                      <span className="line-clamp-2">{r.release_notes ?? '—'}</span>
                    </td>
                    <td className="px-4 py-3">
                      {r.is_active ? (
                        <span className="rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-medium text-green-800">Active</span>
                      ) : (
                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-500">Yanked</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {r.is_active && (
                        <button
                          type="button"
                          onClick={() => yank(r)}
                          disabled={yanking === r.id}
                          className="flex items-center gap-1 rounded px-2 py-1 text-[12px] text-red-500 hover:bg-red-50 disabled:opacity-50"
                        >
                          <AlertTriangle size={12} />
                          Yank
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showPublish && (
        <PublishModal
          onClose={() => setShowPublish(false)}
          onPublished={(r) => { setReleases((prev) => [r, ...prev]); setShowPublish(false) }}
        />
      )}
    </div>
  )
}
